/**
 * OrphanPhotoWatcher
 *
 * Componente globale (monta una sola volta in App.tsx) che intercetta gli
 * eventi WS `request_completed_by_mobile` quando nessuna istanza di
 * RemotePhotoRequest è più montata.
 *
 * Caso d'uso: l'utente desktop ha aperto la modale, qualcuno ha accettato
 * la richiesta, l'utente ha chiuso la modale per fare altro. Quando la foto
 * arriva noi:
 *  1. Scarichiamo il file sul disco (download automatico)
 *  2. Mostriamo un toast informativo
 *  3. Liberiamo il requestId dal registro orfani
 *
 * Non interferisce con il flusso normale: gestiamo SOLO i requestId
 * registrati come orfani in `orphanPhotoRequests.ts`. Se una modale è
 * ancora aperta lei processerà l'evento normalmente.
 */

import { useEffect } from 'react';
import { registerMessageHandler } from '../../hooks/useSharedWebSocket';
import { useToast } from '../ui/ToastProvider';
import {
  isOrphanRequest,
  getOrphanRequestMeta,
  clearOrphanRequest,
} from './orphanPhotoRequests';

export function OrphanPhotoWatcher() {
  const { showToast } = useToast();

  useEffect(() => {
    const handler = async (data: any) => {
      const requestId = data?.requestId as string | undefined;
      if (!requestId) return;
      if (!isOrphanRequest(requestId)) return;

      const meta = getOrphanRequestMeta(requestId);
      const photos = Array.isArray(data?.photos) ? data.photos : [];
      if (photos.length === 0) {
        // niente da scaricare
        clearOrphanRequest(requestId);
        return;
      }

      try {
        for (let i = 0; i < photos.length; i++) {
          const raw = photos[i];
          const url = typeof raw === 'string' ? raw : raw?.url;
          if (!url) continue;
          const fullUrl = url.startsWith('/') ? `${window.location.origin}${url}` : url;

          const resp = await fetch(fullUrl);
          if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
          const blob = await resp.blob();
          const downloadUrl = URL.createObjectURL(blob);

          const a = document.createElement('a');
          a.href = downloadUrl;
          const fromTag = (meta?.from || 'mobile').replace(/[^a-zA-Z0-9-]/g, '_');
          a.download = `foto_${fromTag}_${requestId.slice(0, 8)}_${i + 1}.webp`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(downloadUrl), 30_000);
        }

        showToast({
          type: 'success',
          title: '📸 Foto ricevuta',
          message:
            `${meta?.from || 'Operatore'} ha completato "${meta?.title || 'richiesta foto'}". ` +
            `${photos.length === 1 ? 'File scaricato' : `${photos.length} file scaricati`}.`,
          duration: 9000,
        });
      } catch (err) {
        console.error('[OrphanPhotoWatcher] errore download foto orfana:', err);
        showToast({
          type: 'error',
          title: '📸 Foto ricevuta ma scarico fallito',
          message:
            'La foto è sul server ma non è stato possibile scaricarla in automatico. ' +
            'Riprova dalla schermata "Richieste foto".',
          duration: 10000,
        });
      } finally {
        clearOrphanRequest(requestId);
      }
    };

    const unregister = registerMessageHandler('request_completed_by_mobile', handler);
    return unregister;
  }, [showToast]);

  return null;
}

export default OrphanPhotoWatcher;
